/**
 * Wrappers around Cesium's EntityCollection / DataSourceCollection /
 * ImageryLayerCollection. Expose a uniform MIGIS-flavoured surface so
 * developers never call `viewer.entities.add(new Cesium.Entity(...))`
 * or `viewer.imageryLayers.addImageryProvider(...)` directly.
 *
 * All three classes work even when the wrapped collection is null
 * (i.e. Cesium isn't loaded) — calls become no-ops and reads return [].
 */

import { EventEmitter } from '../events/MapEvent';

export interface MICollectionEvent<T = any> {
  type: 'added' | 'removed' | 'changed';
  item: T;
}

abstract class BaseCollection<T = any> extends EventEmitter {
  protected items: T[] = [];
  protected idIndex: Map<string, T> = new Map();

  /** Total entries currently in the collection. */
  get length(): number { return this.items.length; }

  /** Iterable. Use `for (const item of collection)`. */
  [Symbol.iterator](): IterableIterator<T> { return this.items[Symbol.iterator](); }

  /** Snapshot array (safe to mutate by the caller). */
  toArray(): T[] { return [...this.items]; }

  /** True if the given item (or one with matching id) is in the collection. */
  contains(itemOrId: T | string): boolean {
    if (typeof itemOrId === 'string') return this.idIndex.has(itemOrId);
    return this.items.includes(itemOrId);
  }

  /** Look up by id. */
  getById(id: string): T | undefined { return this.idIndex.get(id); }

  abstract removeAll(): void;
}

// ─── Entity collection ─────────────────────────────────────────────────

export class MIEntityCollection extends BaseCollection<any> {
  private _native: any;

  constructor(nativeCollection: any | null) {
    super();
    this._native = nativeCollection;
    if (nativeCollection?.values) this.items = [...nativeCollection.values];
    for (const e of this.items) {
      if (e?.id) this.idIndex.set(String(e.id), e);
    }
    if (nativeCollection?.collectionChanged?.addEventListener) {
      nativeCollection.collectionChanged.addEventListener((_c: any, added: any[], removed: any[], changed: any[]) => {
        for (const a of added ?? []) { this.items.push(a); if (a?.id) this.idIndex.set(String(a.id), a); this.emit('added', { item: a }); }
        for (const r of removed ?? []) {
          const i = this.items.indexOf(r); if (i >= 0) this.items.splice(i, 1);
          if (r?.id) this.idIndex.delete(String(r.id));
          this.emit('removed', { item: r });
        }
        for (const c of changed ?? []) this.emit('changed', { item: c });
      });
    }
  }

  /**
   * Add an entity. Accepts either a Cesium.Entity (when caller controls the
   * shape) or a plain options bag forwarded to `viewer.entities.add(opts)`.
   * Returns the inserted native entity.
   */
  add(entityOrOptions: any): any {
    if (this._native?.add) return this._native.add(entityOrOptions);
    // Fallback: behave like an in-memory list when no engine.
    this.items.push(entityOrOptions);
    if (entityOrOptions?.id) this.idIndex.set(String(entityOrOptions.id), entityOrOptions);
    this.emit('added', { item: entityOrOptions });
    return entityOrOptions;
  }

  /** Remove by reference, by id, or by predicate. Returns true if anything was removed. */
  remove(target: any | string | ((item: any) => boolean)): boolean {
    if (typeof target === 'function') {
      let removed = false;
      for (const it of [...this.items]) if (target(it)) removed = this.remove(it) || removed;
      return removed;
    }
    if (typeof target === 'string') {
      const e = this.idIndex.get(target);
      return !!e && this.remove(e);
    }
    if (this._native?.remove) return !!this._native.remove(target);
    const i = this.items.indexOf(target);
    if (i < 0) return false;
    this.items.splice(i, 1);
    if (target?.id) this.idIndex.delete(String(target.id));
    this.emit('removed', { item: target });
    return true;
  }

  removeAll(): void {
    if (this._native?.removeAll) this._native.removeAll();
    else this.items = [];
    this.idIndex.clear();
  }
}

// ─── Data source collection ────────────────────────────────────────────

export class MIDataSourceCollection extends BaseCollection<any> {
  private _native: any;

  constructor(nativeCollection: any | null) {
    super();
    this._native = nativeCollection;
    if (nativeCollection?.length) {
      for (let i = 0; i < nativeCollection.length; i++) {
        const ds = nativeCollection.get(i);
        this.items.push(ds);
        if (ds?.name) this.idIndex.set(ds.name, ds);
      }
    }
    if (nativeCollection?.dataSourceAdded?.addEventListener) {
      nativeCollection.dataSourceAdded.addEventListener((_col: any, ds: any) => {
        this.items.push(ds); if (ds?.name) this.idIndex.set(ds.name, ds);
        this.emit('added', { item: ds });
      });
    }
    if (nativeCollection?.dataSourceRemoved?.addEventListener) {
      nativeCollection.dataSourceRemoved.addEventListener((_col: any, ds: any) => {
        const i = this.items.indexOf(ds); if (i >= 0) this.items.splice(i, 1);
        if (ds?.name) this.idIndex.delete(ds.name);
        this.emit('removed', { item: ds });
      });
    }
    if (nativeCollection?.dataSourceMoved?.addEventListener) {
      nativeCollection.dataSourceMoved.addEventListener((ds: any) => {
        this.emit('changed', { item: ds });
      });
    }
  }

  /** Programmatically signal a 'changed' event for an existing data source. */
  notifyChanged(ds: any): void { this.emit('changed', { item: ds }); }

  /** Add a data source (Cesium DataSource or Promise<DataSource>). */
  add(ds: any): Promise<any> | any {
    if (this._native?.add) return this._native.add(ds);
    this.items.push(ds);
    if (ds?.name) this.idIndex.set(ds.name, ds);
    this.emit('added', { item: ds });
    return ds;
  }

  remove(ds: any, destroy = false): boolean {
    if (this._native?.remove) return !!this._native.remove(ds, destroy);
    const i = this.items.indexOf(ds);
    if (i < 0) return false;
    this.items.splice(i, 1);
    if (ds?.name) this.idIndex.delete(ds.name);
    this.emit('removed', { item: ds });
    return true;
  }

  removeAll(destroy = false): void {
    if (this._native?.removeAll) this._native.removeAll(destroy);
    else this.items = [];
    this.idIndex.clear();
  }
}

// ─── Imagery layer collection ──────────────────────────────────────────

export class MIImageryLayerCollection extends BaseCollection<any> {
  private _native: any;

  constructor(nativeCollection: any | null) {
    super();
    this._native = nativeCollection;
    if (nativeCollection?.length) {
      for (let i = 0; i < nativeCollection.length; i++) this.items.push(nativeCollection.get(i));
    }
    if (nativeCollection?.layerAdded?.addEventListener) {
      nativeCollection.layerAdded.addEventListener((layer: any) => { this.items.push(layer); this.emit('added', { item: layer }); });
    }
    if (nativeCollection?.layerRemoved?.addEventListener) {
      nativeCollection.layerRemoved.addEventListener((layer: any) => {
        const i = this.items.indexOf(layer); if (i >= 0) this.items.splice(i, 1);
        this.emit('removed', { item: layer });
      });
    }
    if (nativeCollection?.layerMoved?.addEventListener) {
      nativeCollection.layerMoved.addEventListener((layer: any) => {
        this.emit('changed', { item: layer });
      });
    }
    if (nativeCollection?.layerShownOrHidden?.addEventListener) {
      nativeCollection.layerShownOrHidden.addEventListener((layer: any) => {
        this.emit('changed', { item: layer });
      });
    }
  }

  /** Programmatically signal a 'changed' event for an existing imagery layer. */
  notifyChanged(layer: any): void { this.emit('changed', { item: layer }); }

  /** Add an imagery layer from a Cesium ImageryProvider (or pre-built layer). */
  add(providerOrLayer: any, index?: number): any {
    if (this._native?.addImageryProvider && providerOrLayer && !providerOrLayer._imageryProvider) {
      return this._native.addImageryProvider(providerOrLayer, index);
    }
    if (this._native?.add && providerOrLayer?._imageryProvider) {
      return this._native.add(providerOrLayer, index);
    }
    this.items.push(providerOrLayer);
    this.emit('added', { item: providerOrLayer });
    return providerOrLayer;
  }

  remove(layer: any, destroy = true): boolean {
    if (this._native?.remove) return !!this._native.remove(layer, destroy);
    const i = this.items.indexOf(layer);
    if (i < 0) return false;
    this.items.splice(i, 1);
    this.emit('removed', { item: layer });
    return true;
  }

  removeAll(destroy = true): void {
    if (this._native?.removeAll) this._native.removeAll(destroy);
    else this.items = [];
  }

  raise(layer: any): void { this._native?.raise?.(layer); }
  lower(layer: any): void { this._native?.lower?.(layer); }
  raiseToTop(layer: any): void { this._native?.raiseToTop?.(layer); }
  lowerToBottom(layer: any): void { this._native?.lowerToBottom?.(layer); }
}
